import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AuthenticatedUser } from '@fametrue/shared';
import { useAuth } from '../auth/AuthProvider';
import { SUPPORTED_LANGUAGES, setLanguage, type Language } from '../i18n';
import { MOBILE_QUERY, useMediaQuery } from '../lib/useMediaQuery';

function initialsOf(user: AuthenticatedUser): string {
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
}

/**
 * Blocco destro della top bar. Sotto i 900 px non renderizza nulla: lì il menu
 * utente sta nell'header, nello slot `.sb-user` di `AppSidebar`. Il riferimento
 * dà a `.topbar-right` un `flex: 1 1 100%`, quindi lasciandolo qui finirebbe su
 * una riga propria staccata dal resto.
 */
export function TopbarUser({ user }: { user: AuthenticatedUser }): ReactNode {
  const compactNav = useMediaQuery(MOBILE_QUERY);
  if (compactNav) return null;

  return (
    <div className="topbar-right">
      <div className="topbar-sep" />
      <UserMenu user={user} />
    </div>
  );
}

/**
 * Avatar con dropdown: identità, lingua e disconnessione. È l'unico punto di
 * accesso a lingua e logout, quindi deve essere lo stesso in ogni sezione —
 * dashboard e pannello di amministrazione compresi.
 */
export function UserMenu({ user }: { user: AuthenticatedUser }): ReactNode {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const current: Language = i18n.language.startsWith('en') ? 'en' : 'it';

  return (
    <div className="user-menu" ref={container}>
      <div
        className="topbar-avatar"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('shell.userMenu')}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        {initialsOf(user)}
      </div>

      {open ? (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-head">
            <div className="user-menu-name">
              {user.firstName} {user.lastName}
            </div>
            <div className="user-menu-mail">{user.email}</div>
            <span className="user-menu-role">
              {user.role === 'admin'
                ? t('shell.roleAdmin')
                : (user.companyName ?? t('shell.roleClient'))}
            </span>
          </div>

          <div className="user-menu-section">{t('language.label')}</div>
          <div className="user-menu-langs">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                className="user-menu-lang"
                aria-pressed={language === current}
                onClick={() => void setLanguage(language)}
              >
                {t(`language.${language}`)}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="user-menu-action"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void navigate('/password');
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="4" y="10" width="16" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 018 0v3" />
            </svg>
            {t('shell.changePassword')}
          </button>

          <button
            type="button"
            className="user-menu-action"
            role="menuitem"
            onClick={() => void logout()}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            {t('shell.logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
